import React, { useEffect, useState } from "react";
import AppShell from "../../components/argus/AppShell";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "../../components/ui/dialog";
import {
  ShieldCheck, KeyRound, Cloud, Plus, CheckCircle2, AlertTriangle, XCircle, Loader2, Trash2,
  RefreshCw, Lock, Building2, Globe, Server, Radar,
} from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";

const PROVIDER_ICON = {
  entra: Building2, google: Globe, saml: ShieldCheck, ldap: Server,
  aws: Cloud, azure: Cloud, gcp: Cloud,
  zap: Radar,
};

const FIELD_LABELS = {
  tenant_id: "Tenant ID",
  client_id: "Client ID",
  client_secret: "Client secret",
  default_role: "Default role for new users",
  workspace_domain: "Workspace domain (example.com)",
  idp_entity_id: "IdP entity ID",
  idp_sso_url: "IdP SSO URL",
  idp_certificate: "IdP signing certificate (PEM)",
  host: "Host",
  port: "Port",
  use_tls: "Use LDAPS / StartTLS",
  bind_dn: "Bind DN",
  bind_password: "Bind password",
  base_dn: "Base DN",
  user_filter: "User filter (LDAP query)",
  access_key_id: "AWS access key ID",
  secret_access_key: "AWS secret access key",
  default_region: "Default region",
  assume_role_arn: "Assume-role ARN (optional)",
  subscription_id: "Subscription ID",
  project_id: "GCP project ID",
  service_account_json: "Service-account JSON",
  zap_base_url: "ZAP daemon URL (e.g. http://zap.yourdomain:8090)",
  zap_api_key: "ZAP API key",
};

const ROLES = [
  { v: "viewer", label: "Viewer (read-only)" },
  { v: "analyst", label: "Analyst (triage)" },
  { v: "admin", label: "Admin" },
];

const SECRET_FIELDS = new Set([
  "client_secret", "secret_access_key", "bind_password", "service_account_json", "idp_certificate", "zap_api_key",
]);

const STATUS_PILL = {
  ga: { label: "GA", cls: "bg-safe/15 text-safe border-safe/30" },
  preview: { label: "Preview", cls: "bg-gold/15 text-gold border-gold/30" },
};

function StatusPill({ status }) {
  const s = STATUS_PILL[status] || STATUS_PILL.preview;
  return (
    <span className={`text-[10px] uppercase tracking-wider border rounded-full px-2 py-0.5 ${s.cls}`}>{s.label}</span>
  );
}

function TestBadge({ test_status, message }) {
  if (test_status === "ok") return (
    <span className="inline-flex items-center gap-1 text-xs text-safe">
      <CheckCircle2 size={12} /> Verified
    </span>
  );
  if (test_status === "failed") return (
    <span className="inline-flex items-center gap-1 text-xs text-sev-high" title={message}>
      <XCircle size={12} /> Failed — {message?.slice(0, 60)}
    </span>
  );
  return <span className="inline-flex items-center gap-1 text-xs text-ink/40"><AlertTriangle size={12} /> Untested</span>;
}

function ProviderDialog({ provider, existing, onSaved }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(() => {
    const base = {};
    for (const f of provider.fields) base[f] = existing?.config?.[f] ?? "";
    return base;
  });
  const [saving, setSaving] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = async () => {
    setSaving(true);
    try {
      const cleanForm = { ...form };
      // Don't send back the redacted display value for secret fields if user didn't edit them
      for (const sf of SECRET_FIELDS) {
        const v = cleanForm[sf];
        if (typeof v === "string" && v.startsWith("…")) delete cleanForm[sf];
      }
      await api.post("/integrations", {
        provider_id: provider.id,
        label: provider.label,
        config: cleanForm,
        enabled: true,
      });
      toast.success(`${provider.label} saved.`);
      setOpen(false);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          className="border-line bg-transparent text-xs"
          data-testid={`config-${provider.id}`}
        >
          {existing ? "Edit" : "Connect"}
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-panel border-line text-ink sm:max-w-lg max-h-[85vh] overflow-y-auto" data-testid={`dialog-${provider.id}`}>
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            {provider.label} <StatusPill status={provider.status} />
          </DialogTitle>
          <DialogDescription className="text-ink/65 mt-1">{provider.blurb}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          {provider.fields.map((f) => (
            <div key={f} className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-ink/60">
                {FIELD_LABELS[f] || f}
              </Label>
              {f === "default_role" ? (
                <Select value={form[f] || "analyst"} onValueChange={update(f)}>
                  <SelectTrigger className="bg-paper border-line"><SelectValue placeholder="Pick a role" /></SelectTrigger>
                  <SelectContent>{ROLES.map((r) => <SelectItem key={r.v} value={r.v}>{r.label}</SelectItem>)}</SelectContent>
                </Select>
              ) : f === "service_account_json" || f === "idp_certificate" ? (
                <Textarea
                  rows={5}
                  value={form[f] || ""}
                  onChange={update(f)}
                  placeholder={SECRET_FIELDS.has(f) ? "Paste the full content. Stored encrypted-at-rest." : ""}
                  className="bg-paper border-line text-ink font-mono text-xs resize-none"
                  data-testid={`field-${f}`}
                />
              ) : f === "use_tls" ? (
                <Select value={String(form[f] ?? "true")} onValueChange={(v) => setForm((s) => ({ ...s, [f]: v === "true" }))}>
                  <SelectTrigger className="bg-paper border-line"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="true">Yes — LDAPS/StartTLS required</SelectItem>
                    <SelectItem value="false">No (not recommended)</SelectItem>
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  type={SECRET_FIELDS.has(f) ? "password" : "text"}
                  value={form[f] ?? ""}
                  onChange={update(f)}
                  placeholder={SECRET_FIELDS.has(f) && existing ? "Leave blank to keep existing" : ""}
                  className="bg-paper border-line text-ink"
                  data-testid={`field-${f}`}
                />
              )}
            </div>
          ))}
        </div>
        <DialogFooter className="mt-4 gap-2">
          <Button variant="outline" onClick={() => setOpen(false)} className="border-line bg-transparent">Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-gold text-paper hover:bg-gold2 font-semibold">
            {saving ? <Loader2 size={14} className="animate-spin mr-2" /> : null}
            {existing ? "Save changes" : "Save & test"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ProviderRow({ provider, existing, onChanged }) {
  const Icon = PROVIDER_ICON[provider.id] || KeyRound;
  const [testing, setTesting] = useState(false);
  const [scanning, setScanning] = useState(false);

  const test = async () => {
    if (!existing) return;
    setTesting(true);
    try {
      const r = await api.post(`/integrations/${existing.id}/test`, {});
      r.data?.ok ? toast.success(`Connected. ${r.data?.message || ""}`) : toast.error(r.data?.message || "Test failed.");
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test failed.");
    } finally {
      setTesting(false);
    }
  };

  const remove = async () => {
    if (!existing) return;
    if (!confirm(`Remove ${provider.label} connection?`)) return;
    try {
      await api.delete(`/integrations/${existing.id}`);
      toast.success("Connection removed.");
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't remove.");
    }
  };

  const scan = async () => {
    if (!existing) return;
    setScanning(true);
    try {
      const r = await api.post(`/integrations/${existing.id}/scan`, {});
      toast.success(`Scan queued. Watch the Findings page — they'll appear as buckets are checked.`);
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't start scan.");
    } finally {
      setScanning(false);
    }
  };

  const canScan = existing && provider.id === "aws" && existing.test_status === "ok";

  return (
    <div className="rounded-xl border border-line bg-panel p-5 flex items-start gap-4" data-testid={`provider-${provider.id}`}>
      <div className="w-10 h-10 rounded-lg bg-paper border border-line flex items-center justify-center shrink-0">
        <Icon size={18} className="text-gold" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="font-display text-xl">{provider.label}</h3>
          <StatusPill status={provider.status} />
          {existing && <TestBadge test_status={existing.test_status} message={existing.test_message} />}
        </div>
        <p className="text-sm text-ink/60 mt-1">{provider.blurb}</p>
        {existing && existing.test_status === "ok" && existing.test_detail && (
          <p className="text-xs text-ink/40 font-mono mt-2 truncate">{Object.entries(existing.test_detail).slice(0, 2).map(([k, v]) => `${k}=${typeof v === "string" ? v : JSON.stringify(v)}`).join(" · ")}</p>
        )}
      </div>
      <div className="flex flex-col gap-1.5 shrink-0">
        <ProviderDialog provider={provider} existing={existing} onSaved={onChanged} />
        {existing && (
          <>
            <Button size="sm" variant="outline" onClick={test} disabled={testing} className="border-line bg-transparent text-xs" data-testid={`test-${provider.id}`}>
              {testing ? <Loader2 size={12} className="animate-spin" /> : <><RefreshCw size={12} className="mr-1" /> Test</>}
            </Button>
            {canScan && (
              <Button size="sm" onClick={scan} disabled={scanning} className="bg-gold text-paper hover:bg-gold2 text-xs font-semibold" data-testid={`scan-${provider.id}`}>
                {scanning ? <Loader2 size={12} className="animate-spin" /> : <><Radar size={12} className="mr-1" /> Scan now</>}
              </Button>
            )}
            <button onClick={remove} className="text-xs text-sev-high hover:opacity-70 self-end mr-1" data-testid={`remove-${provider.id}`}>
              <Trash2 size={12} />
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export default function Integrations() {
  const [catalog, setCatalog] = useState(null);
  const [connections, setConnections] = useState([]);
  const [tab, setTab] = useState("identity");

  const refresh = async () => {
    const [c, conns] = await Promise.all([
      api.get("/integrations/catalog"),
      api.get("/integrations"),
    ]);
    setCatalog(c.data);
    setConnections(conns.data || []);
  };

  useEffect(() => { refresh(); }, []);

  const byProvider = Object.fromEntries(connections.map((c) => [c.provider_id, c]));
  const providers = catalog?.[tab] || [];

  return (
    <AppShell title="Integrations">
      <div className="mb-6">
        <p className="text-ink/70 max-w-2xl">
          Connect ArgusSecure to the systems your team already uses. Identity providers govern who can sign in;
          cloud connectors will surface asset and exposure data once their scanners ship.
        </p>
      </div>

      <div className="flex gap-2 border-b border-line mb-6" data-testid="integrations-tabs">
        {[
          { id: "identity", label: "Identity & Active Directory", Icon: ShieldCheck },
          { id: "cloud", label: "Cloud services", Icon: Cloud },
          { id: "appsec", label: "AppSec engines", Icon: Radar },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm border-b-2 -mb-px ${
              tab === t.id ? "border-gold text-gold" : "border-transparent text-ink/60 hover:text-ink"
            }`}
            data-testid={`tab-${t.id}`}
          >
            <t.Icon size={14} /> {t.label}
          </button>
        ))}
      </div>

      {!catalog ? (
        <div className="rounded-xl border border-line bg-panel/40 h-40 animate-pulse" />
      ) : (
        <div className="grid gap-3" data-testid="providers-grid">
          {providers.map((p) => (
            <ProviderRow key={p.id} provider={p} existing={byProvider[p.id]} onChanged={refresh} />
          ))}
        </div>
      )}

      <div className="mt-10 rounded-xl border border-gold/30 bg-gold/5 p-5 text-sm text-ink/80 flex gap-3" data-testid="integrations-note">
        <Lock size={16} className="text-gold shrink-0 mt-0.5" />
        <div>
          <strong className="text-ink">Honest about status.</strong> "GA" providers are wired and tested end-to-end.
          "Preview" providers save & validate today, with scanning rolling out next release. ArgusSecure publishes
          only what's real.
        </div>
      </div>
    </AppShell>
  );
}
